import Button from '@mui/material/Button'
import Container from '@mui/material/Container';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Grid from '@mui/material/Grid';
import CircularProgress from '@mui/material/CircularProgress';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import React from 'react';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { Avatar, CssBaseline, Link } from '@mui/material';

import { ApolloClient, InMemoryCache, gql } from '@apollo/client'
import { ethers } from 'ethers';
// import { BigNumber } from '@ethersproject/bignumber';

import {
  LATEST_BLOCK_QUERY,
  USER_VEJOE_QUERY,
  BOOSTED_PAIRS_QUERY,
  PAIRS_EXCHANGE_QUERY,
  USER_BALANCE_QUERY,
} from './queries';

import {
  LATEST_BLOCK_API_URL,
  VEJOE_API_URL,
  BOOSTED_API_URL,
  EXCHANGE_API_URL,
  ADDRESS_LABELS,
  RPC_URL,
} from './constants'

// Borrowed from stack overflow...
const leftJoin = (objArr1, objArr2, key1, key2, defaults = {}) => objArr1.map(
  anObj1 => ({
    ...defaults,
    ...objArr2.find(
      anObj2 => anObj1[key1] === anObj2[key2]
    ),
    ...anObj1
  })
);

// Here have created a function to calculate the block number corresponding to a period of time prior to our input block number.
async function getPreviousBlockNumber(blockNumber, timeDelta, averageOver = 100000) {
  const AVAX_PROVIDER = new ethers.providers.JsonRpcProvider(RPC_URL);
  // Info for the input block.
  const rootBlock = await AVAX_PROVIDER.getBlock(blockNumber)
  const rootTimestamp = rootBlock.timestamp
  // Info for the block we use to get recent average blocks per second (so that we make a good first guess).
  const histBlock = await AVAX_PROVIDER.getBlock(blockNumber - averageOver)
  const histTimestamp = histBlock.timestamp
  // Calculate recent average blocks per second.
  const avgBlocksPerSecond = averageOver / (rootTimestamp - histTimestamp)
  // Timestamp that we're aiming for (should be the latest block with a timestamp less than this).
  const targetTimestamp = rootTimestamp - timeDelta
  // Now we start to find the block number we want, kind of by doing a binary search, but with (hopefully) some improvements.
  let newGuessNumber = blockNumber - Math.ceil(timeDelta * avgBlocksPerSecond)
  let newGuessBlock = await AVAX_PROVIDER.getBlock(newGuessNumber)
  let newGuessTimestamp = newGuessBlock.timestamp
  let previousGuessNumber = blockNumber
  while (newGuessTimestamp > targetTimestamp) {
    // This is the scenario that we haven't gone far back enough.
    previousGuessNumber = newGuessNumber
    // Use the original blockNumber here so that in case it's too big, it gets smaller exponentially so we can start.
    newGuessNumber = blockNumber + 2 * (newGuessNumber - blockNumber)
    newGuessBlock = await AVAX_PROVIDER.getBlock(newGuessNumber)
    newGuessTimestamp = newGuessBlock.timestamp
  }
  // Now we should be in a position where the initialGuessTimestamp is at or before our desired timestamp.
  let lbNumber = newGuessNumber
  let ubNumber = previousGuessNumber
  let oneLaterNumber = newGuessNumber + 1
  let oneLaterBlock = await AVAX_PROVIDER.getBlock(oneLaterNumber)
  let oneLaterTimestamp = oneLaterBlock.timestamp
  let found = (newGuessTimestamp <= targetTimestamp && oneLaterTimestamp > targetTimestamp);
  while (!found) {
    newGuessNumber = Math.floor(0.5 * (lbNumber + ubNumber))
    newGuessBlock = await AVAX_PROVIDER.getBlock(newGuessNumber)
    newGuessTimestamp = newGuessBlock.timestamp
    if (newGuessTimestamp > targetTimestamp) {
      ubNumber = newGuessNumber;
    } else {
      lbNumber = newGuessNumber
      oneLaterNumber = newGuessNumber + 1
      oneLaterBlock = await AVAX_PROVIDER.getBlock(oneLaterNumber)
      oneLaterTimestamp = oneLaterBlock.timestamp
      found = (newGuessTimestamp <= targetTimestamp && oneLaterTimestamp > targetTimestamp)
    }
  }
  // At this point our newGuessNumber should be the block we desire
  return newGuessNumber;
}

const theme = createTheme({
  status: {
    danger: '#FFBE40',
  },
  palette: {
    primary: {
      main: 'rgb(136, 141, 239)',
      darker: 'rgb(20, 22, 59)',
      contrastText: 'rgb(225, 228, 245)',
    },
    success: {
      main: 'rgb(242, 113, 106)',
      contrastText: 'rgb(225, 228, 245)',
    },
    background: {
      default: 'rgb(20, 22, 59)',
    },
    others: {
      labels: 'rgb(57, 59, 110)',
      border: 'rgb(126, 132, 255)',
      vejoe: 'rgb(252, 243, 161)',
    },
  },
  typography: {
    h4: {
      color: 'rgb(225, 228, 245)',
    },
    h5: {
      color: 'rgb(225, 228, 245)',
    },
    body1: {
      color: 'rgb(225, 228, 245)',
    },
  },
});

function App() {
  const [veJoeData, setVeJoeData] = React.useState([]);
  const [latestBlock, setLatestBlock] = React.useState("");
  const [latestBlock24H, setLatestBlock24H] = React.useState("");
  const [latestBlock7D, setLatestBlock7D] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [status, setStatus] = React.useState("");
  const [veJoeMedian, setVeJoeMedian] = React.useState("");
  const [veJoeMean, setVeJoeMean] = React.useState("");

  React.useEffect(() => {
    handleRefreshData();
  }, [])

  const formatNumber = (number) => {
    // this puts commas into the number eg 1000 goes to 1,000,
    // i pulled this from stack overflow, i have no idea how it works
    if (number === "loading...") {
      return number;
    } else {
      return Math.floor(number)
        .toString()
        .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    }
  };

  const currencyFormatter = (params) => {
    return '$' + formatNumber(params.value);
  };

  const changeFormatter = (params) => {
    return params.value === 0 ? "-" : (params.value > 0 ? "+" : params.value < 0 ? "-" : "") + formatNumber(params.value);
  };

  const currencyChangeFormatter = (params) => {
    return params.value === 0 ? "-" : (params.value > 0 ? "+" : "") + '$' + formatNumber(params.value);
  };

  const columns = [
    {
      field: 'id', headerName: 'User Address / Label', width: 250, valueFormatter: (params) => { return `${params.value in ADDRESS_LABELS ? ADDRESS_LABELS[params.value]["label"] + " - " : ""}${params.value}` },
      renderCell: (params) => (
        <Grid container alignItems="center" spacing={1}>
          <Grid item xs={6}>
            <Link href={`https://snowtrace.io/address/${params.value}`} target="_blank" color="primary.contrastText">{params.value in ADDRESS_LABELS ? ADDRESS_LABELS[params.value]["label"] : params.value}</Link>
          </Grid>
          <Grid item xs={6}>
            {params.value in ADDRESS_LABELS ? <Button variant="contained" size="small" endIcon={<OpenInNewIcon />} href={ADDRESS_LABELS[params.value]["link"]} target="_blank">Go to site</Button> : ""}
          </Grid>
        </Grid>
      )
    },
    { field: 'inJoeWars', headerName: 'Part of Joe Wars', type: 'string', width: 80 },
    { field: 'joeStaked', headerName: 'JOE Staked', type: 'number', width: 100, valueFormatter: (params) => { return formatNumber(params.value) } },
    {
      field: 'joeStaked_24H',
      headerName: 'Change (24H)',
      type: 'number',
      width: 80,
      valueGetter: (params) => { return params.value === "loading..." ? params.value : params.row.joeStaked - params.row.joeStaked_24H },
      valueFormatter: changeFormatter,
    },
    {
      field: 'joeStaked_7D',
      headerName: 'Change (7D)',
      type: 'number',
      width: 80,
      valueGetter: (params) => { return params.value === "loading..." ? params.value : params.row.joeStaked - params.row.joeStaked_7D },
      valueFormatter: changeFormatter,
    },
    { field: 'joeStakedUSD', headerName: 'JOE Staked USD', type: 'number', width: 100, valueFormatter: currencyFormatter },
    { field: 'totalVeJoeMinted', headerName: 'Total veJOE Minted', type: 'number', width: 100, valueFormatter: (params) => { return formatNumber(params.value) } },
    { field: 'totalVeJoeBurned', headerName: 'Total veJOE Burned', type: 'number', width: 100, valueFormatter: (params) => { return formatNumber(params.value) } },
    { field: 'veJoeBalance', headerName: 'veJOE Balance', type: 'number', width: 100, valueFormatter: (params) => { return formatNumber(params.value) } },
    {
      field: 'veJoeBalance_24H',
      headerName: 'Change (24H)',
      type: 'number',
      width: 100,
      valueGetter: (params) => { return params.value === "loading..." ? params.value : params.row.veJoeBalance - params.row.veJoeBalance_24H },
      valueFormatter: changeFormatter,
    },
    {
      field: 'veJoeBalance_7D',
      headerName: 'Change (7D)',
      type: 'number',
      width: 100,
      valueGetter: (params) => { return params.value === "loading..." ? params.value : params.row.veJoeBalance - params.row.veJoeBalance_7D || params.value },
      valueFormatter: changeFormatter,
    },
    { field: 'boostedFarmBalanceUSD', headerName: 'Boosted TVL USD', type: 'number', width: 100, valueFormatter: currencyFormatter },
    {
      field: 'boostedFarmBalanceUSD_24H',
      headerName: 'Change (24H)',
      type: 'number',
      width: 100,
      valueGetter: (params) => { return params.value === "loading..." ? params.value : params.row.boostedFarmBalanceUSD - params.row.boostedFarmBalanceUSD_24H },
      valueFormatter: currencyChangeFormatter,
    },
    {
      field: 'boostedFarmBalanceUSD_7D',
      headerName: 'Change (7D)',
      type: 'number',
      width: 100,
      valueGetter: (params) => { return params.value === "loading..." ? params.value : params.row.boostedFarmBalanceUSD - params.row.boostedFarmBalanceUSD_7D },
      valueFormatter: currencyChangeFormatter,
    },
    { field: 'joeStakedRanking', headerName: 'Ranking (Staked JOE)', type: 'number', width: 80, valueFormatter: (params) => { return formatNumber(params.value) } },
    { field: 'veJoeBalanceRanking', headerName: 'Ranking (veJOE Balance)', type: 'number', width: 80, valueFormatter: (params) => { return formatNumber(params.value) } },
    { field: 'boostedFarmBalanceRanking', headerName: 'Ranking (Boosted TVL)', type: 'number', width: 80, valueFormatter: (params) => { return formatNumber(params.value) } },
  ];

  const handleRefreshData = async () => {

    setLoading(true);
    setStatus("Getting latest block number...");

    const latestBlockClient = new ApolloClient({
      uri: LATEST_BLOCK_API_URL,
      cache: new InMemoryCache(),
    });

    let veJoeLatestBlockNumber = ""
    veJoeLatestBlockNumber = await latestBlockClient
      .query({
        query: gql(LATEST_BLOCK_QUERY),
        variables: {
          subgraphName: "traderjoe-xyz/vejoe",
        }
      })
    veJoeLatestBlockNumber = veJoeLatestBlockNumber["data"]["indexingStatusForCurrentVersion"]["chains"][0]["latestBlock"]["number"]

    let boostedLatestBlockNumber = ""
    boostedLatestBlockNumber = await latestBlockClient
      .query({
        query: gql(LATEST_BLOCK_QUERY),
        variables: {
          subgraphName: "traderjoe-xyz/boosted-master-chef",
        }
      })
    boostedLatestBlockNumber = boostedLatestBlockNumber["data"]["indexingStatusForCurrentVersion"]["chains"][0]["latestBlock"]["number"]

    let exchangeLatestBlockNumber = ""
    exchangeLatestBlockNumber = await latestBlockClient
      .query({
        query: gql(LATEST_BLOCK_QUERY),
        variables: {
          subgraphName: "traderjoe-xyz/exchange",
        }
      })
    exchangeLatestBlockNumber = exchangeLatestBlockNumber["data"]["indexingStatusForCurrentVersion"]["chains"][0]["latestBlock"]["number"]

    const newLatestBlock = Math.min.apply(Math, [veJoeLatestBlockNumber, boostedLatestBlockNumber, exchangeLatestBlockNumber]);

    setLatestBlock(newLatestBlock);

    setStatus("Getting veJOE data for latest block...");

    let all_vejoe_users = await retrieveVeJoeData(newLatestBlock);

    all_vejoe_users = all_vejoe_users.sort((a, b) => Number(b.joeStaked) - Number(a.joeStaked))
      .map((user, i) => ({ joeStakedRanking: i + 1, ...user }))
    all_vejoe_users = all_vejoe_users.sort((a, b) => Number(b.veJoeBalance) - Number(a.veJoeBalance))
      .map((user, i) => ({ veJoeBalanceRanking: i + 1, ...user }))

    let numUsers = all_vejoe_users.length
    let vejoe1 = all_vejoe_users.find(user => user.veJoeBalanceRanking === Math.floor(numUsers / 2)).veJoeBalance
    let vejoe2 = all_vejoe_users.find(user => user.veJoeBalanceRanking === Math.ceil(numUsers / 2)).veJoeBalance
    setVeJoeMedian(0.5 * (Number(vejoe1) + Number(vejoe2)))
    let veJoeTotal = all_vejoe_users.map(user => Number(user.veJoeBalance)).reduce((a, b) => a + b);
    setVeJoeMean(veJoeTotal / numUsers)

    all_vejoe_users = all_vejoe_users.map((user, i) => ({
      inJoeWars: user.id in ADDRESS_LABELS,
      boostedFarmBalanceUSD: 'loading...',
      boostedFarmBalanceRanking: 'loading...',
      joeStaked_24H: 'loading...',
      joeStakedUSD_24H: 'loading...',
      totalVeJoeMinted_24H: 'loading...',
      totalVeJoeBurned_24H: 'loading...',
      veJoeBalance_24H: 'loading...',
      joeStakedRanking_24H: 'loading...',
      veJoeBalanceRanking_24H: 'loading...',
      joeStaked_7D: 'loading...',
      joeStakedUSD_7D: 'loading...',
      totalVeJoeMinted_7D: 'loading...',
      totalVeJoeBurned_7D: 'loading...',
      veJoeBalance_7D: 'loading...',
      joeStakedRanking_7D: 'loading...',
      veJoeBalanceRanking_7D: 'loading...',
      boostedFarmBalanceUSD_24H: 'loading...',
      boostedFarmBalanceRanking_24H: 'loading...',
      boostedFarmBalanceUSD_7D: 'loading...',
      boostedFarmBalanceRanking_7D: 'loading...',
      ...user
    }))
    // May as well update the data now before getting the rest of the information (which can be slower).
    setVeJoeData(all_vejoe_users);

    setStatus("Getting TVL data for latest block...");

    // Now get the farm TVL data.
    let all_users = await retrieveTVLData(newLatestBlock);

    setStatus("Combining and aggregating...");

    // Now we join the data together
    all_users = leftJoin(all_vejoe_users.map(({ boostedFarmBalanceUSD, boostedFarmBalanceRanking, ...others }) => others), all_users, "id", "address", { "boostedFarmBalanceUSD": 0 });

    // And add the ranking
    all_users = all_users.sort((a, b) => Number(b.boostedFarmBalanceUSD) - Number(a.boostedFarmBalanceUSD))
      .map((user, i) => ({ boostedFarmBalanceRanking: i + 1, ...user }))

    // And finally we update our state.
    setVeJoeData(all_users);

    // Now we begin to get the data for the previous 24 hours and the previous 7 days
    setStatus("Getting block number for 24 hours ago...");
    const previousBlockNumber24Hours = await getPreviousBlockNumber(newLatestBlock, 60 * 60 * 24)
    setLatestBlock24H(previousBlockNumber24Hours)

    setStatus("Getting block number for 7 days ago...");
    const previousBlockNumber7Days = await getPreviousBlockNumber(newLatestBlock, 60 * 60 * 24 * 7)
    setLatestBlock7D(previousBlockNumber7Days)

    setStatus("Getting veJOE data for 24H ago...");

    let all_vejoe_users_24_hours = await retrieveVeJoeData(previousBlockNumber24Hours);
    all_vejoe_users_24_hours = all_vejoe_users_24_hours.map(({
      id,
      joeStaked,
      joeStakedUSD,
      totalVeJoeMinted,
      totalVeJoeBurned,
      veJoeBalance,
    }) => ({
      id_24H: id,
      joeStaked_24H: joeStaked,
      joeStakedUSD_24H: joeStakedUSD,
      totalVeJoeMinted_24H: totalVeJoeMinted,
      totalVeJoeBurned_24H: totalVeJoeBurned,
      veJoeBalance_24H: veJoeBalance,
    }))

    setStatus("Getting veJOE data for 7D ago...");
    let all_vejoe_users_7_days = await retrieveVeJoeData(previousBlockNumber7Days);
    all_vejoe_users_7_days = all_vejoe_users_7_days.map(({
      id,
      joeStaked,
      joeStakedUSD,
      totalVeJoeMinted,
      totalVeJoeBurned,
      veJoeBalance,
    }) => ({
      id_7D: id,
      joeStaked_7D: joeStaked,
      joeStakedUSD_7D: joeStakedUSD,
      totalVeJoeMinted_7D: totalVeJoeMinted,
      totalVeJoeBurned_7D: totalVeJoeBurned,
      veJoeBalance_7D: veJoeBalance,
    }))

    all_users = leftJoin(all_users.map(({
      joeStaked_24H,
      joeStakedUSD_24H,
      totalVeJoeMinted_24H,
      totalVeJoeBurned_24H,
      veJoeBalance_24H,
      joeStakedRanking_24H,
      veJoeBalanceRanking_24H,
      joeStaked_7D,
      joeStakedUSD_7D,
      totalVeJoeMinted_7D,
      totalVeJoeBurned_7D,
      veJoeBalance_7D,
      joeStakedRanking_7D,
      veJoeBalanceRanking_7D,
      ...others
    }) => others),
      all_vejoe_users_24_hours, "id", "id_24H", {
      joeStaked_24H: 0,
      joeStakedUSD_24H: 0,
      totalVeJoeMinted_24H: 0,
      totalVeJoeBurned_24H: 0,
      veJoeBalance_24H: 0,
    });

    all_users = all_users.sort((a, b) => Number(b.joeStaked_24H) - Number(a.joeStaked_24H))
      .map((user, i) => ({ joeStakedRanking_24H: i + 1, ...user }))
    all_users = all_users.sort((a, b) => Number(b.veJoeBalance_24H) - Number(a.veJoeBalance_24H))
      .map((user, i) => ({ veJoeBalanceRanking_24H: i + 1, ...user }))

    all_users = leftJoin(all_users, all_vejoe_users_7_days, "id", "id_7D", {
      joeStaked_7D: 0,
      joeStakedUSD_7D: 0,
      totalVeJoeMinted_7D: 0,
      totalVeJoeBurned_7D: 0,
      veJoeBalance_7D: 0,
    });

    all_users = all_users.sort((a, b) => Number(b.joeStaked_7D) - Number(a.joeStaked_7D))
      .map((user, i) => ({ joeStakedRanking_7D: i + 1, ...user }))
    all_users = all_users.sort((a, b) => Number(b.veJoeBalance_7D) - Number(a.veJoeBalance_7D))
      .map((user, i) => ({ veJoeBalanceRanking_7D: i + 1, ...user }))

    setVeJoeData(all_users);

    // Now get the 24H and 7D data for TVL.

    setStatus("Getting TVL data for 24H ago...");

    let all_users_24_hours = await retrieveTVLData(previousBlockNumber24Hours);

    all_users_24_hours = all_users_24_hours.map(({
      address,
      boostedFarmBalanceUSD,
    }) => ({
      address_24H: address,
      boostedFarmBalanceUSD_24H: boostedFarmBalanceUSD,
    }))

    all_users = leftJoin(all_users.map(({
      boostedFarmBalanceUSD_24H,
      boostedFarmBalanceRanking_24H,
      boostedFarmBalanceUSD_7D,
      boostedFarmBalanceRanking_7D,
      ...others
    }) => others),
      all_users_24_hours, "id", "address_24H", {
      boostedFarmBalanceUSD_24H: 0,
    });

    all_users = all_users.sort((a, b) => Number(b.boostedFarmBalanceUSD_24H) - Number(a.boostedFarmBalanceUSD_24H))
      .map((user, i) => ({ boostedFarmBalanceRanking_24H: i + 1, ...user }))

    setStatus("Getting TVL data for 7D ago...");

    let all_users_7_days = await retrieveTVLData(previousBlockNumber7Days);

    all_users_7_days = all_users_7_days.map(({
      address,
      boostedFarmBalanceUSD,
    }) => ({
      address_7D: address,
      boostedFarmBalanceUSD_7D: boostedFarmBalanceUSD,
    }))

    all_users = leftJoin(all_users, all_users_7_days, "id", "address_7D", { boostedFarmBalanceUSD_7D: 0, });

    all_users = all_users.sort((a, b) => Number(b.boostedFarmBalanceUSD_7D) - Number(a.boostedFarmBalanceUSD_7D))
      .map((user, i) => ({ boostedFarmBalanceRanking_7D: i + 1, ...user }))

    setVeJoeData(all_users);
    setStatus("");
    setLoading(false);
  };

  const retrieveVeJoeData = async (blockNumber) => {
    // Start by getting all of the information for veJOE stakers.
    const veJoeClient = new ApolloClient({
      uri: VEJOE_API_URL,
      cache: new InMemoryCache(),
    });

    let last_vejoe_id = "";
    let all_vejoe_users = [];
    let new_vejoe_users = [0];

    while (new_vejoe_users.length > 0) {
      new_vejoe_users = await veJoeClient
        .query({
          query: gql(USER_VEJOE_QUERY),
          variables: {
            id: last_vejoe_id,
            latestBlock: Number(blockNumber),
          },
        })
      new_vejoe_users = new_vejoe_users["data"]["users"]
      all_vejoe_users = all_vejoe_users.concat(new_vejoe_users)
      if (new_vejoe_users.length > 0) {
        last_vejoe_id = new_vejoe_users[new_vejoe_users.length - 1]["id"]
      }
    }
    return all_vejoe_users
  }

  const retrieveTVLData = async (blockNumber) => {

    // Now we get the pairs which are under the boosted masterchef contract
    const boostedPairsClient = new ApolloClient({
      uri: BOOSTED_API_URL,
      cache: new InMemoryCache(),
    });
    let boostedPairs = []
    boostedPairs = await boostedPairsClient
      .query({
        query: gql(BOOSTED_PAIRS_QUERY),
        variables: {
          latestBlock: Number(blockNumber),
        }
      })
    boostedPairs = boostedPairs["data"]["pools"].map((pool) => pool.pair)

    // Here we get the data to show TVL for the pairs, from which we can get TVL for the farm pool, and by extension, for individual addresses.
    const exchangeClient = new ApolloClient({
      uri: EXCHANGE_API_URL,
      cache: new InMemoryCache(),
    });

    let pairs = [];
    pairs = await exchangeClient
      .query({
        query: gql(PAIRS_EXCHANGE_QUERY),
        variables: {
          id_list: boostedPairs,
          latestBlock: Number(blockNumber),
        }
      })
    pairs = pairs["data"]["pairs"]

    // Finally we get the balances for users in all of the boosted farms
    const userBoostedClient = new ApolloClient({
      uri: BOOSTED_API_URL,
      cache: new InMemoryCache(),
    });

    let last_id = "";
    let all_users = [];
    let new_users = [0];

    while (new_users.length > 0) {
      new_users = await userBoostedClient
        .query({
          query: gql(USER_BALANCE_QUERY),
          variables: {
            id: last_id,
            latestBlock: Number(blockNumber),
          },
        })
      new_users = new_users["data"]["users"]
      all_users = all_users.concat(new_users)
      if (new_users.length > 0) {
        last_id = new_users[new_users.length - 1]["id"]
      }
    }
    all_users = all_users.map((user) => ({
      id: user.id,
      pool_id: user.pool.id,
      pool_pair: user.pool.pair,
      pool_balance: user.pool.balance,
      pool_jlp_balance: user.pool.jlpBalance,
      address: user.address,
      amount: user.amount,
    }))
    all_users = leftJoin(all_users, pairs, "pool_pair", "id")

    // Here we do the calculation/aggregation to get the individual user TVLs
    all_users = all_users.map((user) => ({
      address: user.address,
      boostedFarmBalanceUSD: (Number(user.amount) / Number(user.pool_balance)) * Number(user.pool_jlp_balance) * Number(user.reserveUSD) / Number(user.totalSupply)
    }))
    // Now we aggregate per address
    all_users = all_users.reduce((res, row) => {
      if (!(row.address in res)) {
        res.__array.push(res[row.address] = row);
      } else {
        res[row.address].boostedFarmBalanceUSD += row.boostedFarmBalanceUSD;
      }
      return res;
    }, { __array: [] }).__array

    return all_users;
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Container maxWidth="xl" sx={{
        height: '100vh',
        padding: "10px",
        '& .super-app-theme--label': {
          bgcolor: "others.labels",
        }
      }}>
        <Grid container spacing={2} align="center" sx={{ height: '100%', }}>
          <Grid container item alignItems="center">
            <Grid item xs={3} sm={1}>
              <Avatar sx={{ bgcolor: 'others.vejoe' }} src="https://traderjoexyz.com//static/media/veJoe.192d0354.png" >
              </Avatar>
            </Grid>
            <Grid item xs={9} sm={4}>
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant="h4">veJOE Leaderboard</Typography>
                </Grid>
                {veJoeMean === "" ? "" :
                  <Grid item xs={12}>
                    <Typography variant="body1">Avg veJoe Balance: {formatNumber(veJoeMean)}</Typography>
                  </Grid>
                }
                {veJoeMedian === "" ? "" :
                  <Grid item xs={12}>
                    <Typography variant="body1">Median veJoe Balance: {formatNumber(veJoeMedian)}</Typography>
                  </Grid>
                }
              </Grid>
            </Grid>
            <Grid item xs={7} sm={4}>
              <Grid container>
                <Grid item xs={12}>
                  <Typography variant="h5">Data as of block: {latestBlock}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">-24H block: {latestBlock24H}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="body1">-7D block: {latestBlock7D}</Typography>
                </Grid>
                {
                  status === "" ? "" :
                    <Grid item xs={12}>
                      <Typography variant="body1">{status}</Typography>
                    </Grid>
                }
              </Grid>
            </Grid>
            <Grid item xs={5} sm={3}>
              <Box sx={{ m: 1, position: 'relative' }}>
                <Button
                  variant="contained"
                  onClick={handleRefreshData}
                  disabled={loading}
                >
                  Refresh data
                </Button>
                {loading && (
                  <CircularProgress
                    size={24}
                    sx={{
                      color: "white",
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      marginTop: '-12px',
                      marginLeft: '-12px',
                    }}
                  />
                )}
              </Box>
            </Grid>
          </Grid>
          <Grid item xs={12} height={"90vh"}>
            <DataGrid
              rows={veJoeData}
              columns={columns}
              density='compact'
              headerHeight={80}
              // pageSize={20}
              initialState={{
                sorting: {
                  sortModel: [{ field: 'joeStaked', sort: 'desc' }],
                },
                columns: {
                  columnVisibilityModel: {
                    inJoeWars: false,
                    totalVeJoeMinted: false,
                    totalVeJoeBurned: false,
                  }
                },
                filter: {
                  filterModel: {
                    items: [{ columnField: 'inJoeWars', operatorValue: 'equals', value: 'true' }]
                  }
                }
              }}
              components={{ Toolbar: GridToolbar }}
              rowsPerPageOptions={[10, 25, 50, 100]}
              getRowClassName={(params) => `super-app-theme--${params.row.id in ADDRESS_LABELS ? "label" : "other"}`}
              disableSelectionOnClick
              sx={{
                boxShadow: 2,
                border: 2,
                borderColor: 'others.border',
                color: 'primary.contrastText',
                '& .MuiDataGrid-cell:hover': {
                  color: 'others.border',
                },
                '& .MuiDataGrid-columnHeaders': {
                  backgroundColor: 'success.main',
                },
                '& .MuiDataGrid-columnHeaderTitle': {
                  textOverflow: "clip",
                  whiteSpace: "break-spaces",
                  lineHeight: 1
                },
                '& .MuiTablePagination-root': {
                  color: 'primary.contrastText',
                },
                '& .MuiSelect-icon': {
                  color: 'primary.contrastText',
                },
                '& .MuiButtonBase-root': {
                  color: 'primary.contrastText',
                },
                '& .MuiMenuItem-root': {
                  color: 'primary.darker',
                },
              }}
            />
          </Grid>
        </Grid>
      </Container>
    </ThemeProvider>
  );
}

export default App;
